import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { DeliveryWorkFilter, DeliveryWorkOrder, DeliveryWorkSession } from '../../models/delivery-work';
import work from '../../services/orders/delivery-work.service';

const time = (value?: string) => value ? new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hourCycle: 'h23' }) : '—';
const duration = (minutes?: number | null) => {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  const total = Math.round(minutes);
  const h = Math.floor(total / 60), m = total % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};
const coords = (lat?: number, lng?: number) => lat != null && lng != null ? `${lat}, ${lng}` : '—';
const DeliveryWorkReport = () => {
  const [filter, setFilter] = useState<DeliveryWorkFilter>({ page: 1, pageSize: 25 });
  const [applied, setApplied] = useState<DeliveryWorkFilter>(filter);
  const [rows, setRows] = useState<DeliveryWorkSession[]>([]);
  const [total, setTotal] = useState(0);
  const [executives, setExecutives] = useState<{ id: number; name: string; phone: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<DeliveryWorkSession>();
  const [orders, setOrders] = useState<DeliveryWorkOrder[]>([]);
  const [ordersBusy, setOrdersBusy] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const request = useRef(0);
  const detailRequest = useRef(0);
  const invalidate = useCallback(() => { request.current++; detailRequest.current++; }, []);
  const load = useCallback(async (query: DeliveryWorkFilter) => {
    const id = ++request.current;
    setBusy(true); setError('');
    try {
      const result = await work.report(query);
      if (id === request.current) { setRows(result.rows); setTotal(result.totalCount); }
    } catch { if (id === request.current) { setRows([]); setTotal(0); setError('Unable to load the report. Check your report permission and retry.'); } }
    finally { if (id === request.current) setBusy(false); }
  }, []);
  useEffect(() => {
    let active = true;
    void work.executives().then(value => { if (active) setExecutives(value); })
      .catch(() => { if (active) setError('Unable to load executives. Check report permission.'); });
    return () => { active = false; invalidate(); };
  }, [invalidate]);
  useEffect(() => { void load(applied); }, [applied, load]);
  const open = async (row: DeliveryWorkSession) => {
    const id = ++detailRequest.current;
    setSelected(row); setOrders([]); setOrdersError(''); setOrdersBusy(true);
    try { const details = await work.orders(row.executiveId, row.deliveryDate.slice(0, 10)); if (id === detailRequest.current) setOrders(details); }
    catch { if (id === detailRequest.current) setOrdersError('Unable to load assigned orders. Please close and retry.'); }
    finally { if (id === detailRequest.current) setOrdersBusy(false); }
  };
  const page = (e: DataTablePageEvent) => setApplied({ ...applied, page: Math.floor(e.first / e.rows) + 1, pageSize: e.rows });
  const location = (row: DeliveryWorkSession, start: boolean) => <div>
    <div>{coords(start ? row.startLatitude : row.endLatitude, start ? row.startLongitude : row.endLongitude)}</div>
    <div>{(start ? row.startLocationReason : row.endLocationReason) ?? (start ? row.startLocationStatus : row.endLocationStatus) ?? '—'}</div>
    {(start ? row.startAccuracyMeters : row.endAccuracyMeters) != null && <small>Accuracy ±{start ? row.startAccuracyMeters : row.endAccuracyMeters} m</small>}
  </div>;
  return <div className="card">
    <h3>Delivery Start / End Report</h3>
    <p>All recorded and assigned delivery days are listed by default. Times are IST. Select Orders to view assignment status and invoice locations.</p>
    <div className="flex flex-wrap gap-3 align-items-end mb-3">
      <label>From date<input className="p-inputtext block" aria-label="From date" type="date" value={filter.fromDate ?? ''}
        onChange={e => setFilter({ ...filter, fromDate: e.target.value || undefined })} /></label>
      <label>To date<input className="p-inputtext block" aria-label="To date" type="date" value={filter.toDate ?? ''}
        onChange={e => setFilter({ ...filter, toDate: e.target.value || undefined })} /></label>
      <div><label htmlFor="work-executive" className="block">Executive</label><Dropdown inputId="work-executive" value={filter.executiveId}
        options={executives.map(e => ({ label: `${e.name} (${e.phone})`, value: e.id }))} placeholder="All executives" filter showClear
        onChange={e => setFilter({ ...filter, executiveId: e.value ?? undefined })} /></div>
      <div><label htmlFor="work-status" className="block">Status</label><Dropdown inputId="work-status" value={filter.status}
        options={['Not started', 'Started', 'Ended']} placeholder="All statuses" showClear
        onChange={e => setFilter({ ...filter, status: e.value ?? undefined })} /></div>
      <Button label="Filter" icon="pi pi-filter" disabled={busy} onClick={() => {
        if (filter.fromDate && filter.toDate && filter.fromDate > filter.toDate) { setError('To date must be on or after From date.'); return; }
        setApplied({ ...filter, page: 1 });
      }} />
      <Button label="Reset" outlined disabled={busy} onClick={() => { const next = { page: 1, pageSize: 25 }; setFilter(next); setApplied(next); }} />
      <Button label="Refresh" icon="pi pi-refresh" outlined loading={busy} onClick={() => void load(applied)} />
    </div>
    {error && <p role="alert" className="text-red-600">{error}</p>}
    <DataTable value={rows} loading={busy} lazy paginator first={(applied.page - 1) * applied.pageSize} rows={applied.pageSize}
      rowsPerPageOptions={[25, 50, 100]} totalRecords={total} onPage={page} scrollable tableStyle={{ minWidth: '85rem' }}
      onRowClick={e => void open(e.data as DeliveryWorkSession)} rowClassName={() => 'cursor-pointer'} emptyMessage="No delivery activity found.">
      <Column field="deliveryDate" header="Delivery date" body={(r: DeliveryWorkSession) => r.deliveryDate.slice(0, 10)} />
      <Column field="executiveName" header="Executive" /><Column field="phone" header="Phone" />
      <Column field="status" header="Status" /><Column field="assignedOrders" header="Assigned orders" />
      <Column header="Started (IST)" body={(r: DeliveryWorkSession) => time(r.startedAtUtc)} />
      <Column header="Ended (IST)" body={(r: DeliveryWorkSession) => time(r.endedAtUtc)} />
      <Column header="Duration" body={(r: DeliveryWorkSession) => duration(r.durationMinutes)} />
      <Column header="Start location" body={(r: DeliveryWorkSession) => location(r, true)} />
      <Column header="End location" body={(r: DeliveryWorkSession) => location(r, false)} />
      <Column header="Details" body={(r: DeliveryWorkSession) => <Button label="Orders" outlined onClick={e => { e.stopPropagation(); void open(r); }} />} />
    </DataTable>
    <Dialog header={selected ? `${selected.executiveName} — ${selected.deliveryDate.slice(0, 10)}` : 'Assigned orders'} visible={!!selected}
      onHide={() => { detailRequest.current++; setSelected(undefined); }} modal style={{ width: '95vw' }}>
      <p>Distance is straight-line distance in metres from the shipped address GPS to the invoice GPS. It is not driving distance.
        Each partial invoice is listed separately. Missing location data is shown as unavailable.</p>
      {ordersError && <p role="alert" className="text-red-600">{ordersError}</p>}
      <DataTable value={orders} loading={ordersBusy} paginator rows={20} rowsPerPageOptions={[20, 50, 100]} scrollable
        tableStyle={{ minWidth: '90rem' }} emptyMessage="No assigned orders found.">
        <Column field="sequenceNo" header="Sequence" sortable /><Column field="orderId" header="Order #" sortable />
        <Column field="customerName" header="Customer" sortable /><Column field="status" header="Assignment status" />
        <Column field="shipmentAddress" header="Shipped address" body={(r: DeliveryWorkOrder) => r.shipmentAddress ?? 'Unavailable'} />
        <Column field="shipmentGps" header="Shipped GPS" body={(r: DeliveryWorkOrder) => r.shipmentGps ?? 'Unavailable'} />
        <Column field="invoiceNo" header="Invoice #" body={(r: DeliveryWorkOrder) => r.invoiceNo ?? '—'} />
        <Column header="Invoiced (IST)" body={(r: DeliveryWorkOrder) => time(r.invoicedAtUtc)} />
        <Column header="Invoice GPS" body={(r: DeliveryWorkOrder) => <div>{coords(r.invoiceLatitude, r.invoiceLongitude)}
          <div>{r.invoiceLocationReason ?? r.invoiceLocationStatus ?? 'Unavailable'}</div>
          {r.invoiceAccuracyMeters != null && <small>Accuracy ±{r.invoiceAccuracyMeters} m</small>}</div>} />
        <Column field="distanceMeters" header="Distance (m)" sortable body={(r: DeliveryWorkOrder) => r.distanceMeters ?? 'Unavailable'} />
      </DataTable>
    </Dialog>
  </div>;
};
export default DeliveryWorkReport;
